import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GameProps, GameDef, playAreaStyle } from './types';

// ── WHACK-A-MOLE ─────────────────────────────────────────────────────────────
// Critters pop out of a 3x3 grid of holes; tap them before they duck back.
// Pace quickens as the timer runs down. 15s.

const GAME_SECONDS = 15;
const HOLES = 9; // 3x3
const CRITTERS = ['🐹', '🐰', '🦔', '🐸', '🐱', '🐶'];
const BONK = ['💫', '⭐', '💥'];

interface Mole {
  hole: number;
  emoji: string;
  bonked: boolean;
}

function WhackAMole({ theme, isKid, onEnd }: GameProps) {
  const [timeLeft, setTimeLeft] = useState(GAME_SECONDS);
  const [score, setScore] = useState(0);
  const [moles, setMoles] = useState<Record<number, Mole>>({});
  const [bonk, setBonk] = useState<{ hole: number; emoji: string } | null>(null);
  const scoreRef = useRef(0);
  const timeRef = useRef(GAME_SECONDS);

  useEffect(() => {
    if (timeLeft <= 0) {
      onEnd({ score: scoreRef.current, noun: scoreRef.current === 1 ? 'critter' : 'critters' });
      return;
    }
    timeRef.current = timeLeft;
    const t = setTimeout(() => setTimeLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, onEnd]);

  // Pop a mole into a random free hole at an accelerating cadence.
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;

    const loop = () => {
      if (!alive) return;
      setMoles(prev => {
        const free = Array.from({ length: HOLES }, (_, i) => i).filter(h => !(h in prev));
        if (free.length === 0) return prev;
        const hole = free[Math.floor(Math.random() * free.length)];
        const emoji = CRITTERS[Math.floor(Math.random() * CRITTERS.length)];
        const next = { ...prev, [hole]: { hole, emoji, bonked: false } };
        // critter ducks back on its own after a lifetime that shrinks over time
        const lifetime = Math.max(650, 1300 - (GAME_SECONDS - timeRef.current) * 45);
        setTimeout(() => {
          setMoles(cur => {
            if (cur[hole] && !cur[hole].bonked) {
              const { [hole]: _gone, ...rest } = cur;
              return rest;
            }
            return cur;
          });
        }, lifetime);
        return next;
      });
      // spawn cadence also tightens as the clock runs down
      const gap = Math.max(450, 900 - (GAME_SECONDS - timeRef.current) * 30);
      timer = setTimeout(loop, gap);
    };

    timer = setTimeout(loop, 500);
    return () => { alive = false; clearTimeout(timer); };
  }, []);

  const whack = useCallback((hole: number) => {
    setMoles(prev => {
      const m = prev[hole];
      if (!m || m.bonked) return prev;
      scoreRef.current += 1;
      setScore(scoreRef.current);
      setBonk({ hole, emoji: BONK[Math.floor(Math.random() * BONK.length)] });
      setTimeout(() => setBonk(null), 400);
      const { [hole]: _gone, ...rest } = prev;
      return rest;
    });
  }, []);

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontWeight: 800, fontSize: isKid ? '1.3rem' : '1.1rem',
      }}>
        <span>🔨 {score}</span>
        <span style={{ color: timeLeft <= 5 ? '#ef4444' : theme.accent }}>⏱️ {timeLeft}s</span>
      </div>

      <div style={{
        ...playAreaStyle(theme),
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: 'repeat(3, 1fr)',
        gap: '8px',
        padding: '14px',
      }}>
        {Array.from({ length: HOLES }, (_, hole) => {
          const m = moles[hole];
          const showBonk = bonk?.hole === hole;
          return (
            <button
              key={hole}
              onClick={() => whack(hole)}
              aria-label={m ? 'Whack the critter' : 'Empty hole'}
              style={{
                position: 'relative',
                background: `radial-gradient(ellipse at center bottom, ${theme.accent}33, ${theme.light}55)`,
                border: `2px solid ${theme.light}`,
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isKid ? '2.6rem' : '2.2rem',
                overflow: 'hidden',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {showBonk ? (
                <span style={{ animation: 'reward-burst 0.4s ease-out forwards' }}>{bonk!.emoji}</span>
              ) : m ? (
                <span style={{ animation: 'reward-pop-in 0.18s ease-out', lineHeight: 1 }}>{m.emoji}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </>
  );
}

export const WHACK_A_MOLE: GameDef = {
  id: 'whack-a-mole',
  title: 'Whack-a-Critter',
  emoji: '🐹',
  instructions: 'Tap the critters when they pop out — but be quick, they duck back!',
  verb: 'Whack',
  Component: WhackAMole,
};

export default WhackAMole;
